/**
 * Shared utilities for AJAX filtering functionality
 */

/**
 * Debounce function to limit how often a function is called
 */
export function debounce(func, delay) {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
}

/**
 * Loader utilities
 */
export function createLoaderUtils(loader) {
    return {
        show: () => {
            if (loader) loader.classList.remove('hidden');
        },
        hide: () => {
            if (loader) loader.classList.add('hidden');
        }
    };
}

/**
 * Update aria attributes for toggle buttons (like sort buttons)
 */
export function updateAriaChecked(selectedBtn, otherBtn) {
    selectedBtn.setAttribute('aria-checked', 'true');
    otherBtn.setAttribute('aria-checked', 'false');
}

/**
 * Toggle button visual states
 */
export function toggleButtonState(activeBtn, inactiveBtn) {
    activeBtn.classList.add('bg-deque-gray-900', 'text-deque-white');
    activeBtn.classList.remove('text-deque-gray-700');

    inactiveBtn.classList.remove('bg-deque-gray-900', 'text-deque-white');
    inactiveBtn.classList.add('text-deque-gray-700');
}

/**
 * Create search handler
 */
export function createSearchHandler(options) {
    const {
        searchInput,
        clearBtn,
        filters,
        onSearch
    } = options;

    if (!searchInput) return;

    const handleSearch = debounce((value) => {
        filters.search = value.trim();
        onSearch();
    }, 300);

    searchInput.addEventListener('input', (e) => {
        const val = e.target.value;
        if (clearBtn) clearBtn.classList.toggle('hidden', val.trim() === '');
        handleSearch(e.target.value);
    });

    if (clearBtn) {
        clearBtn.addEventListener('click', () => {
            searchInput.value = '';
            clearBtn.classList.add('hidden');
            filters.search = '';
            onSearch();
        });
    }
}

/**
 * Create sort buttons handler (popular/recent)
 */
export function createSortHandler(options) {
    const {
        popularBtn,
        recentBtn,
        filters,
        onSort
    } = options;

    if (!popularBtn || !recentBtn) return;

    popularBtn.addEventListener('click', function() {
        filters.sort = 'popular';
        toggleButtonState(popularBtn, recentBtn);
        updateAriaChecked(popularBtn, recentBtn);
        onSort();
    });

    recentBtn.addEventListener('click', function() {
        filters.sort = 'recent';
        toggleButtonState(recentBtn, popularBtn);
        updateAriaChecked(recentBtn, popularBtn);
        onSort();
    });
}

/**
 * Initialize filters from selected options
 */
export function initializeFiltersFromDOM(section, filters) {
    section.querySelectorAll('.custom-select__option[selected]').forEach(option => {
        const filterKey = option.dataset.filter;
        const value = option.dataset.value;
        if (value !== 'all' && filterKey in filters) {
            filters[filterKey] = value;
        }
    });
}

/**
 * Build filter URL based on current filters and post type
 * @param {Object} filters - Current filter values
 * @param {string} archivePostType - Current archive post type (e.g., 'post', 'resources')
 * @returns {string} - The constructed URL
 */
export function buildFilterUrl(filters, archivePostType = null) {
    let url;

    if (archivePostType === 'post') {
        if (filters.topic) {
            url = `/blog/category/${filters.topic}/`;
        } else {
            url = '/blog/';
        }
    } else if (filters.topic && filters.type) {
        // Resources library (archive + block): virtual type "blog" stays under /resources/.
        url = `/resources/topic/${filters.topic}/type/${filters.type}/`;
    } else if (filters.topic) {
        url = `/resources/topic/${filters.topic}/`;
    } else if (filters.type) {
        url = `/resources/type/${filters.type}/`;
    } else {
        url = '/resources/';
    }

    const params = new URLSearchParams();
    if (filters.date) {
        params.set('date', filters.date);
    }

    const query = params.toString();
    if (query) {
        url += (url.includes('?') ? '&' : '?') + query;
    }

    return url;
}

/**
 * Build filter URL for Blog with Filters block (stays on current page).
 *
 * @param {Object} filters - Current filter values
 * @returns {string}
 */
export function buildBlogBlockFilterUrl(filters) {
    const url = new URL(window.location.href);

    url.searchParams.delete('paged');

    ['topic', 'author', 'tag', 'date', 'search', 'sort'].forEach((key) => {
        if (filters[key]) {
            url.searchParams.set(key, filters[key]);
        } else {
            url.searchParams.delete(key);
        }
    });

    url.pathname = url.pathname.replace(/\/page\/\d+\/?$/, '/');

    return url.pathname + url.search + url.hash;
}
